import axios, { AxiosHeaders, InternalAxiosRequestConfig } from 'axios';
import { auth } from './firebase';
import { getActiveMembershipId, setActiveMembershipId } from './activeMembership';

// Function to determine the correct API base URL
const getApiBaseUrl = (): string => {
  console.log('🔍 Determining API Base URL...');
  console.log('🌍 Current hostname:', window.location.hostname);
  console.log('🌍 Current origin:', window.location.origin);
  console.log('🔧 REACT_APP_API_BASE_URL env var:', process.env.REACT_APP_API_BASE_URL);
  console.log('🔧 NODE_ENV:', process.env.NODE_ENV);
  
  // If explicitly set in environment, use that
  if (process.env.REACT_APP_API_BASE_URL) {
    console.log('✅ Using explicit REACT_APP_API_BASE_URL from environment');
    // Always ensure the base URL ends with /api
    const rawUrl = process.env.REACT_APP_API_BASE_URL;
    // Remove trailing slash for consistency, then add /api if missing
    const sanitized = rawUrl.endsWith('/') ? rawUrl.slice(0, -1) : rawUrl;
    const finalUrl = sanitized.endsWith('/api') ? sanitized : `${sanitized}/api`;
    console.log('🎯 Final API URL from env:', finalUrl);
    return finalUrl;
  }

  // Detect Railway environment
  const currentHost = window.location.hostname;
  const currentOrigin = window.location.origin;
  const isRailway = currentHost.includes('railway.app') || currentHost.includes('.up.railway.app');
  const isLocalhost = currentHost === 'localhost' || currentHost === '127.0.0.1';
  const isProduction = process.env.NODE_ENV === 'production';
  
  console.log('🚂 Is Railway environment:', isRailway);
  console.log('🏠 Is localhost:', isLocalhost);
  console.log('🏭 Is production:', isProduction);
  
  // Force Railway backend URL if we're on Railway or in production
  if (isRailway || (isProduction && !isLocalhost)) {
    console.log('🚂 Detected Railway/Production environment, using Railway backend URL');
    
    // Known Railway backend URL - update this if your backend URL changes
    const backendUrl = 'https://backend-production-5a1b1.up.railway.app/api';
    
    console.log('🎯 Railway backend URL:', backendUrl);
    return backendUrl;
  }
  
  // Local development fallback
  console.log('🏠 Using localhost backend for development');
  const localhostUrl = 'http://localhost:3001/api';
  console.log('🎯 Localhost backend URL:', localhostUrl);
  return localhostUrl;
};

const API_BASE_URL = getApiBaseUrl();

console.log(`🌐 Frontend API Base URL: ${API_BASE_URL}`);

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout to handle email operations
});

// Public API client for unauthenticated endpoints (e.g., matching public responses)
export const publicApiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Cache for user data to avoid repeated API calls
let userDataCache: any = null;
let lastFetchEmail: string | null = null;
let currentAuthUserEmail: string | null = null;

// Function to get user data from backend based on email
const getUserDataFromBackend = async (email: string) => {
  // Use cache if same user
  if (userDataCache && lastFetchEmail === email) {
    return userDataCache;
  }

  try {
    // /users/me answers for the verified login and the remembered agency
    // (x-user-id). With several agencies and none chosen yet it returns
    // needsAgencySelection — the AuthContext then shows the agency picker.
    // validateStatus: accept 404 as valid response to avoid axios error logging
    const activeId = getActiveMembershipId(email);
    const response = await axios.get(`${API_BASE_URL}/users/me`, {
      headers: {
        'Authorization': `Bearer ${await auth.currentUser?.getIdToken()}`,
        'x-user-email': email,
        ...(activeId ? { 'x-user-id': activeId } : {}),
      },
      validateStatus: (status) => status === 200 || status === 404, // Accept both 200 and 404 as valid
    });

    // If 404, return null (user not found, will use fallback)
    if (response.status === 404) {
      return null;
    }

    const userData = response.data.data;
    if (!userData || userData.needsAgencySelection) {
      return null;
    }
    if (userData.id && userData.id !== activeId) {
      setActiveMembershipId(email, userData.id);
    }
    userDataCache = userData;
    lastFetchEmail = email;
    return userData;
  } catch (error: any) {
    // Only log unexpected errors (not 404)
    if (error.response?.status !== 404) {
      console.error('Failed to fetch user data from backend:', error);
    }
    return null;
  }
};

// Get minimal user data for authentication headers
// The backend will validate and return full user data including agencyId
const getMockUserData = async () => {
  const currentUser = auth.currentUser;
  if (!currentUser || !currentUser.email) {
    return null;
  }

  // For the initial request to /users/me, we only need email
  // The backend will look up the user in the database and return full data including agencyId
  // This breaks the circular dependency where frontend needs agencyId to send headers
  // but needs to call backend to get agencyId

  try {
    const backendUserData = await getUserDataFromBackend(currentUser.email);
    if (backendUserData) {
      console.log('✅ Using backend user data:', backendUserData);
      return {
        id: backendUserData.id,
        email: backendUserData.email,
        role: backendUserData.role,
        agencyId: backendUserData.agencyId,
        permissions: backendUserData.permissions,
      };
    }
  } catch (error: any) {
    // If 404, user doesn't exist in backend yet (might be during account setup)
    if (error.response?.status === 404) {
      console.warn(`⚠️ User ${currentUser.email} not found in backend database`);
    } else {
      console.error('❌ Error fetching user data from backend:', error);
    }
  }

  // SECURITY: Only provide minimal fallback for super admin
  if (currentUser.email === 'admin@example.com') {
    console.warn('⚠️ Using super admin fallback');
    return {
      id: '550e8400-e29b-41d4-a716-446655440000',
      email: currentUser.email,
      role: 'SUPER_ADMIN',
      agencyId: undefined,
    };
  }

  // CRITICAL: For all other users, we need backend data
  // Do NOT provide fallback to prevent agency isolation breach
  console.error(`❌ CRITICAL: No backend data found for ${currentUser.email}`);
  console.error('❌ User must be properly set up in the database with agency assignment');

  return null;
};

// Add authentication headers to requests
// Named, damit auch Verbindungen außerhalb von axios (Live-Aktualisierung per
// fetch-Stream) genau dieselben Anmelde-Header bekommen.
const attachAuthHeaders = async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
  const user = auth.currentUser;
  if (user) {
    // Check if user has changed and clear cache if needed
    checkAndClearCacheOnUserChange(user.email);
    
    const token = await user.getIdToken();
    config.headers.Authorization = `Bearer ${token}`;

    // Add mock user data headers for backend authentication
    const userData = await getMockUserData();
    
    // DEBUG: Log what user data we're sending
    console.log('🔐 API CLIENT DEBUG - Sending headers:');
    console.log('  - Firebase user email:', user.email);
    console.log('  - Retrieved userData:', userData);
    
    if (userData) {
      config.headers['x-user-id'] = userData.id;
      config.headers['x-user-email'] = userData.email;
      config.headers['x-user-role'] = userData.role;
      if (userData.agencyId) {
        config.headers['x-user-agency-id'] = userData.agencyId;
      }
      // Send shifts permission level so the backend can enforce write access
      if (userData.permissions) {
        const s = userData.permissions.shifts;
        const shiftsLevel = !s || s === false ? 'none' : s === true ? 'full' : s;
        config.headers['x-user-shifts-level'] = shiftsLevel;

        // Send Ansprechpartner visibility filter if set
        const apIds = userData.permissions.allowedAnsprechpartnerIds;
        if (apIds?.length) {
          config.headers['x-user-allowed-ap-ids'] = apIds.join(',');
        }

        // Send caregiver Ansprechpartner visibility filter if set
        const cgApIds = userData.permissions.allowedCaregiverAnsprechpartnerIds;
        if (cgApIds?.length) {
          config.headers['x-user-allowed-caregiver-ap-ids'] = cgApIds.join(',');
        }

        // Optional visibility restrictions from the Rechteverwaltung (default off)
        if (userData.permissions.tasksOnlyAssignedToMe === true) {
          config.headers['x-user-tasks-only-own'] = 'true';
        }
        if (userData.permissions.shiftsOnlyOwnCaregivers === true) {
          config.headers['x-user-shifts-only-own-caregivers'] = 'true';
        }
      }
      
      console.log('  - Headers being sent:');
      console.log('    - x-user-id:', userData.id);
      console.log('    - x-user-email:', userData.email);
      console.log('    - x-user-role:', userData.role);
      console.log('    - x-user-agency-id:', userData.agencyId || 'undefined');
    } else {
      // No agency chosen yet (picker open): still say which one was remembered,
      // /users/me then answers with the list of agencies.
      const activeId = getActiveMembershipId(user.email);
      config.headers['x-user-email'] = user.email;
      if (activeId) config.headers['x-user-id'] = activeId;
      console.warn('⚠️ No userData found for user:', user.email);
    }
  } else {
    console.log('🔐 No authenticated user found');
    // Clear cache when no user is authenticated
    checkAndClearCacheOnUserChange(null);
  }
  return config;
};
apiClient.interceptors.request.use(attachAuthHeaders);

/** Basis-URL der API (endet auf /api). */
export const getApiUrl = (): string => API_BASE_URL;

/**
 * Die Anmelde-Header eines API-Aufrufs — für Verbindungen, die nicht über
 * axios laufen. null = niemand angemeldet.
 */
export const getApiAuthHeaders = async (): Promise<Record<string, string> | null> => {
  if (!auth.currentUser) return null;
  const config = await attachAuthHeaders({ headers: new AxiosHeaders() } as InternalAxiosRequestConfig);
  const out: Record<string, string> = {};
  Object.entries(config.headers.toJSON()).forEach(([key, value]) => {
    if (value !== undefined && value !== null) out[key] = String(value);
  });
  return out;
};

// Enhanced response interceptor with retry logic and comprehensive error handling
let retryAttempts = new Map<string, number>();
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 1000; // 1 second
const RETRY_MULTIPLIER = 1.5;

apiClient.interceptors.response.use(
  (response) => {
    // Clear retry attempts on successful response
    const requestKey = `${response.config?.method}-${response.config?.url}`;
    retryAttempts.delete(requestKey);
    return response;
  },
  async (error) => {
    const config = error.config;
    const requestKey = `${config?.method}-${config?.url}`;
    const currentAttempts = retryAttempts.get(requestKey) || 0;

    // Enhanced error logging
    const errorDetails = {
      status: error.response?.status,
      statusText: error.response?.statusText,
      message: error.message,
      url: config?.url,
      method: config?.method,
      baseURL: API_BASE_URL,
      attempt: currentAttempts + 1,
      maxAttempts: MAX_RETRY_ATTEMPTS
    };

    // Suppress noisy 404s for file lookups — DocumentCard handles these gracefully
    const isFileLookup404 = error.response?.status === 404 && config?.url?.startsWith('/files/');
    if (!isFileLookup404) {
      console.error('🚨 API Error:', errorDetails);
    }

    // Handle authentication errors
    if (error.response?.status === 401) {
      console.error('🔐 Authentication failed - signing out user');
      await auth.signOut();
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // Handle network errors and server errors with retry logic.
    // Only retry idempotent methods — retrying POST/PUT/PATCH/DELETE on 5xx
    // can silently produce duplicate writes (e.g. duplicate invoices) when the
    // first request actually committed server-side but the response was lost.
    const method = (config?.method || '').toLowerCase();
    const isIdempotentMethod = method === 'get' || method === 'head' || method === 'options';
    const shouldRetry = isIdempotentMethod && (
      error.code === 'NETWORK_ERROR' ||
      error.code === 'ERR_NETWORK' ||
      error.code === 'ECONNABORTED' ||
      (error.response?.status >= 500 && error.response?.status < 600)
    ) && currentAttempts < MAX_RETRY_ATTEMPTS && config && !config._retry;

    if (shouldRetry) {
      const newAttempts = currentAttempts + 1;
      retryAttempts.set(requestKey, newAttempts);
      
      const delay = RETRY_DELAY * Math.pow(RETRY_MULTIPLIER, currentAttempts);
      console.warn(`⏳ Retrying request (${newAttempts}/${MAX_RETRY_ATTEMPTS}) in ${delay}ms...`);
      
      // Mark request as retry to prevent infinite loops
      config._retry = true;
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));
      
      // Retry the request
      return apiClient(config);
    }

    // Log final failure after all retries exhausted
    if (currentAttempts >= MAX_RETRY_ATTEMPTS) {
      console.error('❌ Request failed after maximum retry attempts:', errorDetails);
      retryAttempts.delete(requestKey);
    }
    
    // Enhanced error object with additional context
    const enhancedError = {
      ...error,
      isNetworkError: error.code === 'NETWORK_ERROR' || error.code === 'ERR_NETWORK',
      isTimeout: error.code === 'ECONNABORTED',
      isServerError: error.response?.status >= 500,
      isClientError: error.response?.status >= 400 && error.response?.status < 500,
      retryAttempts: currentAttempts,
      maxRetryAttempts: MAX_RETRY_ATTEMPTS,
      finalAttempt: currentAttempts >= MAX_RETRY_ATTEMPTS
    };
    
    return Promise.reject(enhancedError);
  }
);

// Function to clear the cache (useful for logout or refresh)
export const clearUserDataCache = () => {
  userDataCache = null;
  lastFetchEmail = null;
  currentAuthUserEmail = null;
  console.log('🧹 User data cache cleared');
};

// Function to check if user has changed and clear cache if needed
export const checkAndClearCacheOnUserChange = (newUserEmail: string | null) => {
  if (currentAuthUserEmail && newUserEmail && currentAuthUserEmail !== newUserEmail) {
    console.log('🔄 User changed from', currentAuthUserEmail, 'to', newUserEmail, '- clearing cache');
    clearUserDataCache();
  }
  currentAuthUserEmail = newUserEmail;
}; 